import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FiChevronLeft } from 'react-icons/fi';
import { AccountBoundary, useRequestScope } from '../settings/shared';
import { useExpandedText } from '../notifications/useExpandedText';
import { Field } from '../password/PasswordPage';
import DraftGuard from '../contact/ContactDraftGuard';
import PageLayout from '../../components/layout/PageLayout';
import { authApi } from '../../api/auth';
import { authReturnPath, loginError, validateLoginUser } from '../auth/model';
import { acceptLogin } from '../auth/session';
import { useSignupDraft } from './draft';
import { credentialsError, detailError, latestBirthDate } from './model';
import { Agreement, useSignupStyle, useCleanCallbackUrl } from './shared';
export default function SignupPage({ detail = false }) {
  const location = useLocation();
  return <AccountBoundary key={location.key} component={detail ? Detail : Credentials} />;
}
function Credentials({ user }) {
  const { style, t } = useSignupStyle(),
    location = useLocation(),
    nav = useNavigate(),
    request = useRequestScope(user);
  const target = authReturnPath(new URLSearchParams(location.search).get('redirectUrl'));
  const [f, setF] = useState(() => {
    const d = useSignupDraft.getState().credentials;
    return {
      email: d?.email || '',
      password: d?.password || '',
      confirm: d?.password || '',
      agreed: !!d?.acceptedTerms,
    };
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const lock = useRef(false),
    guard = useRef({ released: false });
  guard.current.dirty = Boolean(f.email || f.password || f.confirm || f.agreed);
  guard.current.busy = busy;
  useCleanCallbackUrl();
  const edit = (key, value) => {
    setF((s) => ({ ...s, [key]: value }));
    setError('');
  };
  const submit = async (e) => {
    e.preventDefault();
    if (lock.current) return;
    const error = credentialsError(f, t);
    if (error) {
      setError(error);
      return;
    }
    lock.current = true;
    setBusy(true);
    setError('');
    const r = request();
    try {
      const result = await authApi.checkEmailStatus(f.email.trim(), r.options);
      if (!r.current()) return;
      if (result?.data?.status === 'DUPLICATED') {
        setError(t('auth.emailDuplicated'));
        return;
      }
      if (result?.data?.status === 'REJOIN_BLOCKED') {
        setError(t('auth.rejoinBlocked'));
        return;
      }
      // Fail closed if the endpoint returned no status, rather than advancing on malformed data.
      if (result?.data?.status !== 'AVAILABLE') {
        setError(t('auth.emailCheckFailed'));
        return;
      }
      useSignupDraft.getState().setCredentials({
        email: f.email.trim(),
        password: f.password,
        acceptedTerms: true,
        acceptedPrivacy: true,
      });
      guard.current.released = true;
      nav('/signup/detail?redirectUrl=' + encodeURIComponent(target));
    } catch (e) {
      if (r.current()) setError(loginError(e, t('auth.emailCheckFailed')));
    } finally {
      if (r.current()) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  if (user) return <Navigate to={target} replace />;
  return (
    <PageLayout paddingTop={0} paddingBottom={0}>
      <div className="pp-settings pp-password pp-signup" style={style}>
        <DraftGuard state={guard} t={t} />
        <header className="pp-settings-header">
          <button
            type="button"
            disabled={busy}
            aria-label={t('common.back')}
            onClick={() =>
              window.history.state?.idx > 0
                ? nav(-1)
                : nav('/login?redirectUrl=' + encodeURIComponent(target))
            }
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>{t('auth.signup')}</h1>
        </header>
        <form aria-label={t('auth.signup')} noValidate onSubmit={submit}>
          <fieldset disabled={busy}>
            <div className="pp-signup-fields">
              <Field
                id="signup-email"
                label={t('auth.email')}
                value={f.email}
                onChange={(v) => edit('email', v)}
                placeholder={t('auth.emailPlaceholder')}
                autoComplete="email"
                required
                t={t}
              />
              <Field
                id="signup-password"
                label={t('auth.password')}
                value={f.password}
                onChange={(v) => edit('password', v)}
                placeholder={t('auth.passwordRuleShort')}
                autoComplete="new-password"
                required
                secret
                t={t}
              />
              <Field
                id="signup-confirm"
                label={t('auth.passwordConfirm')}
                value={f.confirm}
                onChange={(v) => edit('confirm', v)}
                placeholder={t('auth.passwordConfirmPlaceholder')}
                autoComplete="new-password"
                required
                secret
                t={t}
              />
            </div>
            <Agreement checked={f.agreed} onChange={(v) => edit('agreed', v)} t={t} />
            {error && (
              <p role="alert" className="pp-password-error">
                {error}
              </p>
            )}
            <button className="pp-password-submit" type="submit" aria-busy={busy}>
              {t(busy ? 'auth.checking' : 'auth.next')}
            </button>
          </fieldset>
        </form>
      </div>
    </PageLayout>
  );
}
function Detail({ user }) {
  const { style, t } = useSignupStyle(),
    location = useLocation(),
    nav = useNavigate(),
    request = useRequestScope(user),
    expanded = useExpandedText();
  const [params] = useState(() => new URLSearchParams(location.search));
  const [token] = useState(
    () => params.get('signupToken') || useSignupDraft.getState().socialToken,
  );
  const target = authReturnPath(params.get('redirectUrl'));
  const draft = useSignupDraft((s) => s.credentials);
  const [info, setInfo] = useState(null),
    [loading, setLoading] = useState(!!token),
    [attempt, setAttempt] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [f, setF] = useState({
    nickname: '',
    birthDate: '',
    gender: '',
    ageAgreed: false,
    termsAgreed: false,
  });
  const lock = useRef(false),
    guard = useRef({ released: false });
  guard.current.dirty = Boolean(
    f.nickname || f.birthDate || f.gender || f.ageAgreed || f.termsAgreed,
  );
  guard.current.busy = busy;
  useCleanCallbackUrl();
  useEffect(() => {
    if (!token) return;
    let active = true;
    const r = request();
    setLoading(true);
    setError('');
    authApi
      .getSocialSignupInfo(token, { ...r.options, omitPageQuery: true })
      .then((result) => {
        if (!active || !r.current()) return;
        const data = result?.data;
        if (!data || typeof data.email !== 'string' || !data.email)
          throw new Error('Missing social information');
        setInfo(data);
        setF((s) => ({ ...s, nickname: data.nickname || '', birthDate: data.birthDate || '' }));
      })
      .catch(() => {
        if (active && r.current()) setError(t('auth.socialLoginFailed'));
      })
      .finally(() => {
        if (active && r.current()) setLoading(false);
      });
    return () => {
      active = false;
      r.cancel();
    };
    // Locale changes must not reset edited profile fields or repeat the one-time token load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, attempt]);
  const edit = (k, v) => {
    setF((s) => ({ ...s, [k]: v }));
    setError('');
  };
  const submit = async (e) => {
    e.preventDefault();
    if (lock.current) return;
    if (token && !info) {
      setError(t('auth.socialLoginFailed'));
      return;
    }
    const failure = detailError(f, !!token, t);
    if (failure) {
      setError(failure);
      return;
    }
    if (!token && (!draft?.acceptedTerms || !draft?.acceptedPrivacy)) {
      setError(t('auth.agreeTermsRequired'));
      return;
    }
    lock.current = true;
    setBusy(true);
    setError('');
    const r = request();
    try {
      const result = await authApi.signup(
        {
          email: info?.email || draft.email,
          password: token ? '' : draft.password,
          nickname: f.nickname.trim(),
          birthDate: f.birthDate,
          gender: f.gender || null,
          socialSignupToken: token || null,
          acceptedTerms: token ? f.termsAgreed : draft.acceptedTerms,
          acceptedPrivacy: token ? f.termsAgreed : draft.acceptedPrivacy,
          confirmedMinimumAge: f.ageAgreed,
        },
        { ...r.options, omitPageQuery: true },
      );
      if (!r.current()) return;
      let accepted;
      try {
        accepted = validateLoginUser(result?.data, t);
      } catch (e) {
        setError(e.message);
        return;
      }
      guard.current.released = true;
      useSignupDraft.getState().clear();
      acceptLogin(accepted);
      nav(target, { replace: true });
    } catch (e) {
      if (r.current()) setError(loginError(e, t('auth.signupFailed')));
    } finally {
      if (r.current()) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  if (user) return <Navigate to={target} replace />;
  if (!token && (!draft?.email || !draft?.password || !draft?.acceptedTerms))
    return <Navigate to={'/signup?redirectUrl=' + encodeURIComponent(target)} replace />;
  const previous = () =>
    window.history.state?.idx > 0
      ? nav(-1)
      : nav((token ? '/login' : '/signup') + '?redirectUrl=' + encodeURIComponent(target));
  return (
    <PageLayout paddingTop={0} paddingBottom={0}>
      <div
        className={'pp-settings pp-password pp-signup' + (expanded ? ' is-expanded' : '')}
        style={style}
      >
        <DraftGuard state={guard} t={t} />
        <header className="pp-password-reset-header">
          <h1>{t('auth.memberInfo')}</h1>
        </header>
        <div className="pp-signup-email">
          <span>{t('auth.email')}</span>
          <strong>{info?.email || draft?.email || ''}</strong>
        </div>
        {loading ? (
          <p role="status">{t('common.loading')}</p>
        ) : token && !info ? (
          <>
            <p role="alert" className="pp-password-error">
              {error || t('auth.socialLoginFailed')}
            </p>
            <button className="pp-password-submit" onClick={() => setAttempt((v) => v + 1)}>
              {t('common.retry')}
            </button>
          </>
        ) : (
          <form aria-label={t('auth.memberInfo')} noValidate onSubmit={submit}>
            <fieldset disabled={busy}>
              <div className="pp-signup-fields">
                <div className="pp-password-field">
                  <label htmlFor="signup-nickname">
                    {t('auth.nickname')}
                    <span aria-hidden="true"> *</span>
                  </label>
                  <div className="pp-password-input">
                    <input
                      id="signup-nickname"
                      value={f.nickname}
                      onChange={(e) => edit('nickname', e.target.value)}
                      maxLength={14}
                      autoComplete="nickname"
                      placeholder={t('auth.nicknameRule')}
                    />
                  </div>
                </div>
                <div className="pp-password-field">
                  <label htmlFor="signup-birth">
                    {t('auth.birthDate')}
                    <span aria-hidden="true"> *</span>
                  </label>
                  <div className="pp-password-input">
                    <input
                      id="signup-birth"
                      type="date"
                      value={f.birthDate}
                      max={latestBirthDate()}
                      onChange={(e) => edit('birthDate', e.target.value)}
                      autoComplete="bday"
                      required
                    />
                  </div>
                </div>
                <div className="pp-password-field">
                  <span className="pp-signup-label">{t('auth.gender')}</span>
                  <div className="pp-signup-genders">
                    {['male', 'female'].map((v) => (
                      <button
                        type="button"
                        key={v}
                        aria-pressed={f.gender === v}
                        onClick={() => edit('gender', f.gender === v ? '' : v)}
                      >
                        {t(v === 'male' ? 'auth.genderMale' : 'auth.genderFemale')}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {token && (
                <Agreement checked={f.termsAgreed} onChange={(v) => edit('termsAgreed', v)} t={t} />
              )}
              <Agreement checked={f.ageAgreed} onChange={(v) => edit('ageAgreed', v)} t={t} age />
              {error && (
                <p role="alert" className="pp-password-error">
                  {error}
                </p>
              )}
              <button className="pp-password-submit" type="submit" aria-busy={busy}>
                {t(busy ? 'auth.signingUp' : 'auth.signupComplete')}
              </button>
            </fieldset>
          </form>
        )}
        <button className="pp-password-submit secondary" disabled={busy} onClick={previous}>
          {t('auth.previous')}
        </button>
      </div>
    </PageLayout>
  );
}
